import { useState, useEffect } from 'react';
import type { Medication, User } from '@campusmeds/shared';
import { WorkflowStatus } from '@campusmeds/shared';
import { AdminService } from '../services/admin.service';

interface Patient {
  id: string;
  email?: string;
  name?: string;
  phoneNumber?: string;
  medications?: any[];
  createdAt: Date;
  updatedAt: Date;
}

export interface UseAdminDataReturn {
  medications: Medication[];
  users: User[];
  patients: Patient[];
  loading: boolean;
  needsCallCount: number;
  awaitingPatientCount: number;
  problemCount: number;
  totalMedications: number;
  totalUsers: number;
}

export function useAdminData(): UseAdminDataReturn {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeMeds = AdminService.subscribeToMedications((meds) => {
      setMedications(meds);
      setLoading(false);
    });

    const unsubscribeUsers = AdminService.subscribeToUsers((users) => {
      setUsers(users);
    });

    const unsubscribePatients = AdminService.subscribeToPatients((patients) => {
      setPatients(patients);
    });

    return () => {
      unsubscribeMeds();
      unsubscribeUsers();
      unsubscribePatients();
    };
  }, []);

  // Count medications by workflow status
  const needsCallCount = medications.filter(
    m => m.workflowStatus === WorkflowStatus.NEEDS_CALL
  ).length;

  const awaitingPatientCount = medications.filter(
    m => m.workflowStatus === WorkflowStatus.AWAITING_PATIENT
  ).length;

  const problemCount = medications.filter(
    m => m.workflowStatus === WorkflowStatus.PROBLEM
  ).length;

  return {
    medications,
    users,
    patients,
    loading,
    needsCallCount,
    awaitingPatientCount,
    problemCount,
    totalMedications: medications.length,
    totalUsers: users.length
  };
}